import React, {createContext, useCallback, useEffect, useMemo, useState} from 'react';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {SnapPoints, SnapType, Tab} from '~/QuickEcards/types/misc';

type BottomSheetTabContextType = {
  tab: Tab | null;
  setTab: (newTab: Tab) => void;
  snap: SnapType;
  setSnap: (newSnap: SnapType) => void;
  open: boolean;
  setOpen: (newOpen: boolean) => void;
  handleStampClick: (newTab: Tab) => void;
  resetBottomDrawer: () => void;
};

const defaultContextValue: BottomSheetTabContextType = {
  tab: null,
  // biome-ignore lint/suspicious/noEmptyBlockStatements: existing error
  setTab: () => {},
  snap: SnapPoints[0],
  // biome-ignore lint/suspicious/noEmptyBlockStatements: existing error
  setSnap: () => {},
  open: false,
  // biome-ignore lint/suspicious/noEmptyBlockStatements: existing error
  setOpen: () => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: existing error
  handleStampClick: () => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: existing error
  resetBottomDrawer: () => {},
};

export const BottomSheetTabContext = createContext<BottomSheetTabContextType>(defaultContextValue);

export const BottomSheetTabContextParent = ({children}: {children: React.ReactNode}) => {
  const isDesktop = useIsLargishAndUp();
  const [snap, setSnap] = useState<SnapType>(SnapPoints[0]);
  const [tab, setTab] = useState<Tab | null>(isDesktop ? 'design' : null);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    // Default selected tab to Design on desktop if nothing is selected yet
    if (isDesktop) setTab((currTab) => currTab ?? 'design');
  }, [isDesktop]);

  function handleTabChange(newTab: Tab) {
    setTab(newTab);
  }

  function handleSnapChange(newSnap: SnapType) {
    setSnap(newSnap);
  }

  function handleOpenChange(newOpen: boolean) {
    setOpen(newOpen);
  }

  const handleStampClick = useCallback(
    (newTab: Tab) => {
      if (!open) {
        setOpen(!open);
      }

      setSnap(SnapPoints[1]);
      setTab(newTab);
    },
    [open]
  );

  function resetBottomDrawer() {
    setSnap(SnapPoints[0]);
    setTab(null);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const value = useMemo(
    () => ({
      tab,
      setTab: handleTabChange,
      snap,
      setSnap: handleSnapChange,
      open,
      setOpen: handleOpenChange,
      handleStampClick,
      resetBottomDrawer,
    }),
    [handleStampClick, open, snap, tab]
  );

  return <BottomSheetTabContext.Provider value={value}>{children}</BottomSheetTabContext.Provider>;
};
